import { deletePushSubscription, type NotificationSettings, savePushSubscription } from './api.ts';
import { registerServiceWorker } from './service-worker.ts';

/**
 * Notifiche del browser, dal lato della console.
 *
 * Vale solo per lo staff: questa console è l'unica origine su cui esiste un
 * service worker, e non c'è nessun percorso che chieda un permesso di notifica
 * a chi ha chiamato (vincolo 9, sezione 7ter).
 *
 * Su iOS niente di tutto questo funziona finché la pagina non è stata aggiunta
 * alla schermata Home: non è un difetto da aggirare, è come funziona iOS, e va
 * detto in onboarding (sezione 5.7). Per questo lo stato distingue "non
 * disponibile" da "bloccato" da "senza service worker" da "spento": sono
 * problemi diversi con rimedi diversi, e chi è al primo turno non li distingue
 * da solo.
 */

export type PushState =
  /** Il browser non ha né service worker né push: quasi sempre iOS non installato. */
  | 'unsupported'
  /** Il server non ha chiavi VAPID: non è un problema del dispositivo. */
  | 'not_configured'
  /** Il permesso è stato negato: si sblocca dalle impostazioni del sito. */
  | 'denied'
  /**
   * Il service worker non arriva a installarsi su questo dispositivo.
   *
   * È un quarto problema con un quarto rimedio — ricaricare la pagina, o
   * uscire da una finestra privata — e prima non aveva un nome: chi ci finiva
   * vedeva il pulsante restare su "Attivazione…" per sempre, perché
   * `serviceWorker.ready` non è una promessa che fallisce, è una promessa che
   * non si risolve (`docs/DECISIONI.md`, AQ3).
   */
  | 'no_worker'
  /** Si può attivare, e non lo è. */
  | 'off'
  | 'on';

export function pushSupported(): boolean {
  return (
    typeof globalThis.navigator !== 'undefined' &&
    'serviceWorker' in globalThis.navigator &&
    'PushManager' in globalThis &&
    'Notification' in globalThis
  );
}

/** La pagina è stata aggiunta alla schermata Home ed è aperta da lì? */
export function isStandalone(): boolean {
  const navigatorWithStandalone = globalThis.navigator as Navigator & { standalone?: boolean };
  if (navigatorWithStandalone?.standalone === true) return true;
  try {
    return globalThis.matchMedia?.('(display-mode: standalone)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Siamo su un dispositivo Apple, dove le notifiche arrivano solo dalla
 * schermata Home?
 *
 * È un riconoscimento di piattaforma per dare l'istruzione giusta, non un
 * riconoscimento della persona: non si conserva, non si manda al server, e non
 * distingue un dispositivo da un altro (vincolo 1).
 */
export function needsHomeScreen(): boolean {
  const agent = globalThis.navigator?.userAgent ?? '';
  const apple =
    /iPad|iPhone|iPod/.test(agent) || (/Macintosh/.test(agent) && 'ontouchend' in globalThis);
  return apple && !isStandalone();
}

export function pushStateFrom(settings: NotificationSettings | undefined): PushState {
  if (!settings?.pushConfigured) return 'not_configured';
  if (!pushSupported()) return 'unsupported';
  if (globalThis.Notification?.permission === 'denied') return 'denied';
  return settings.subscribed && globalThis.Notification?.permission === 'granted' ? 'on' : 'off';
}

/**
 * Chiede il permesso, si iscrive e manda l'iscrizione al server.
 *
 * Il permesso si chiede **da un gesto**: è il pulsante "Attiva le notifiche",
 * mai al caricamento della pagina — una richiesta di permesso che compare da
 * sola viene negata per riflesso, e il browser ricorda quel no.
 */
export async function enablePush(publicKey: string): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';

  const permission = await globalThis.Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const registration = await readyRegistration();
  if (!registration) return 'no_worker';
  const existing = await registration.pushManager.getSubscription();
  /**
   * Un'iscrizione vecchia con un'altra chiave del server non riceverebbe
   * niente: se le chiavi VAPID sono cambiate, si ricomincia invece di
   * conservare qualcosa che non funziona.
   */
  if (existing && !sameKey(existing, publicKey)) {
    await existing.unsubscribe();
  }

  const subscription =
    existing && sameKey(existing, publicKey)
      ? existing
      : await registration.pushManager.subscribe({
          // Senza payload la notifica non potrebbe dire niente, e il browser
          // ne mostrerebbe una sua: qui il contenuto lo decide il server.
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

  const payload = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!payload.endpoint || !payload.keys?.p256dh || !payload.keys.auth) return 'off';

  await savePushSubscription({
    endpoint: payload.endpoint,
    keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
  });
  return 'on';
}

/**
 * Toglie l'iscrizione da questo dispositivo e dal server.
 *
 * **Il server per primo.** È la metà che conta: è la sua riga a far credere a
 * chi va in turno di essere raggiungibile, e finché c'è, una chiamata smette
 * di far partire l'SMS che sarebbe l'ultima rete. Aspettare prima il service
 * worker vorrebbe dire, per chi è in una finestra privata — cioè per il caso
 * che ha fatto nascere `no_worker` — otto secondi di pulsante fermo prima di
 * arrivare all'unica cosa che doveva succedere.
 */
export async function disablePush(): Promise<PushState> {
  await deletePushSubscription();

  if (pushSupported()) {
    // Se il service worker non c'è, non c'è nemmeno un'iscrizione da togliere
    // da questo dispositivo: quella del server è già andata.
    const registration = await readyRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe();
  }
  return 'off';
}

/**
 * Quanto si aspetta che il service worker sia in servizio.
 *
 * Otto secondi: la registrazione è locale al dispositivo e non passa dalla
 * rete, quindi o succede subito o non succede. Il numero esiste perché
 * `serviceWorker.ready` **non scade da sola** — non è una promessa che
 * fallisce, è una promessa che resta lì — e un pulsante appeso a una promessa
 * che non torna è un pulsante che non funziona e non lo dice.
 */
const WORKER_READY_MS = 8000;

/**
 * Il service worker in servizio, o niente.
 *
 * Due cose insieme, e nessuna delle due basta da sola: **registra** (chi apre
 * le notifiche potrebbe non averlo ancora, e la registrazione si fa una volta
 * sola) e **aspetta con un tetto**, così chi non ce l'ha riceve una risposta
 * invece di un'attesa senza fine.
 *
 * Il tetto copre **tutte e due**, e non è un dettaglio di come è scritta la
 * corsa. La registrazione carica un modulo separato (`virtual:pwa-register`):
 * è una richiesta, e una richiesta può non tornare senza fallire. Aspettarla
 * fuori dalla corsa avrebbe rimesso il difetto di AQ3 una riga sopra la sua
 * correzione, con lo stesso sintomo esatto — "Attivazione…" per sempre e
 * "Prova notifica" che da lì in poi non risponde più.
 */
async function readyRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!pushSupported()) return undefined;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      registerServiceWorker().then(() => globalThis.navigator.serviceWorker.ready),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), WORKER_READY_MS);
      }),
    ]);
  } catch {
    return undefined;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function sameKey(subscription: PushSubscription, publicKey: string): boolean {
  const key = subscription.options?.applicationServerKey;
  if (!key) return false;
  const current = new Uint8Array(key as ArrayBuffer);
  const wanted = urlBase64ToUint8Array(publicKey);
  return current.length === wanted.length && current.every((byte, index) => byte === wanted[index]);
}

/**
 * La chiave VAPID viaggia in base64 sicuro per URL; `subscribe` vuole byte.
 * Sedici righe che ogni progetto con le push riscrive uguali.
 */
export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = globalThis.atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index);
  }
  return bytes;
}
